import React from "react";
import { InputNumber } from "antd";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, noSteppers, radii, withAlpha } from "src/theme/ui";

const MAX_YEARS = 30;
const MAX_MONTHS = 11;

interface Props {
  years: number;
  months: number;
  handleMonthsChange: (value: number | undefined) => void;
  handleYearsChange: (value: number | undefined) => void;
}

/**
 * The duration, to the month.
 *
 * Two numbers in one field rather than two fields: a duration is one
 * quantity, and splitting it into "Years" and "Months" boxes that each
 * validate against the other is already awkward enough without drawing them
 * as two separate controls.
 *
 * EACH HALF IS A LABEL, and that is the fix for the thing that made this
 * unusable. The inputs were 34px wide with the steppers turned off, so the
 * only way to change the years was to hit a two-character target exactly:
 * clicking the word "yrs", or the space around the digit, focused nothing,
 * and the field read as though the year simply could not be changed.
 * Wrapping each half in a label makes the whole half the hit area, and the
 * two halves together cover the box.
 *
 * The halves are equal, and so are the two suffixes. "yrs" is a third wider
 * than "mo", so with each side sized to its own contents the months half
 * came out visibly smaller and the two digits sat at different distances
 * from their own edges.
 */
const Box = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: stretch;
  flex: none;
  width: 156px;
  height: 34px;
  border-radius: ${radii.control}px;
  border: 1px solid ${line.hairline};
  background: ${colors.white};
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${colors.gray[300]};
  }

  &:focus-within {
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${withAlpha(colors.primary[500], 0.14)};
  }
`;

/* A label, so the whole half — digit, gap and unit — focuses the input. */
const Unit = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  padding: 0 8px;
  cursor: text;

  & + & {
    border-left: 1px solid ${line.hairline};
  }
`;

const NumberInput = styled(InputNumber)`
  ${noSteppers}

  &.ant-input-number {
    width: 100%;
    min-width: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
  }

  .ant-input-number-input {
    height: 30px;
    padding: 0;
    text-align: right;
    font-size: 13.5px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${colors.gray[900]};
  }

  &.ant-input-number-focused {
    box-shadow: none;
    border-color: transparent;
  }
`;

/* One width for both, so the digits line up across the two halves however
   long the word after them is. */
const Suffix = styled.span`
  flex: none;
  width: 22px;
  font-size: 11.5px;
  color: ${colors.gray[500]};
`;

const DurationInput: React.FC<Props> = ({
  years,
  months,
  handleMonthsChange,
  handleYearsChange,
}) => (
  <Box>
    <Unit>
      <NumberInput
        controls={false}
        onChange={handleYearsChange}
        value={years}
        min={months ? 0 : 1}
        max={MAX_YEARS}
        aria-label="Years"
      />
      <Suffix>yrs</Suffix>
    </Unit>
    <Unit>
      <NumberInput
        controls={false}
        value={months}
        onChange={handleMonthsChange}
        min={years ? 0 : 6}
        max={MAX_MONTHS}
        aria-label="Months"
      />
      <Suffix>mo</Suffix>
    </Unit>
  </Box>
);

export default DurationInput;
